import { zodResolver } from "@hookform/resolvers/zod";
import { XIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { RichTextEditorInput, Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Sheet, SheetClose, SheetContent } from "@/components/ui/sheet";

import { useCreateJobNote } from "@/hooks/mutations/useCreateJobNote";
import { useUpdateJobNote } from "@/hooks/mutations/useUpdateJobNote";
import { useAddJobNote } from "@/hooks/sets/useAddJobNote";
import { useSetJobNote } from "@/hooks/sets/useSetJobNote";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { JobNote } from "@/types";

const jobNoteSchema = z.object({
  content: z.string().min(1, {
    message: "Required",
  }),
  title: z.string().min(1, {
    message: "Required",
  }),
});

const JobNoteForm = ({
  jobNote,
  handleSuccess,
}: {
  jobNote: JobNote | null;
  handleSuccess: () => void;
}) => {
  const { mutate: createJobNote, isPending: isCreatingJobNote } =
    useCreateJobNote();
  const { mutate: updateJobNote, isPending: isUpdatingJobNote } =
    useUpdateJobNote();

  const addJobNote = useAddJobNote();
  const setJobNote = useSetJobNote();

  const form = useForm<z.infer<typeof jobNoteSchema>>({
    defaultValues: {
      content: jobNote?.content ?? "",
      title: jobNote?.title ?? "",
    },
    resolver: zodResolver(jobNoteSchema),
  });

  const handleSubmit = (data: z.infer<typeof jobNoteSchema>) => {
    mixpanelUtils.trackFormSubmission("Save job note", MIXPANEL_SCREENS.JOB);
    if (jobNote) {
      updateJobNote(
        {
          jobNoteId: jobNote.id,
          updateJobNoteRequest: data,
        },
        {
          onSuccess: (updatedJobNote) => {
            setJobNote({ jobNoteToUpdate: updatedJobNote });
            form.reset();
            handleSuccess();
          },
        },
      );
    } else {
      createJobNote(
        { createJobNoteRequest: data },
        {
          onSuccess: (createdJobNote) => {
            addJobNote({ jobNoteToAdd: createdJobNote });
            form.reset();
            handleSuccess();
          },
        },
      );
    }
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-6"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem className="text-slate-700">
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem className="text-slate-700">
              <FormLabel>Note</FormLabel>
              <FormControl>
                <RichTextEditorInput
                  editorContentClasses="pb-20"
                  mode={{
                    onChange: field.onChange,
                    type: "html",
                    value: field.value,
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Row className="justify-end">
          <Button
            className="rounded-full"
            disabled={isCreatingJobNote || isUpdatingJobNote}
            type="submit"
          >
            Save
          </Button>
        </Row>
      </form>
    </Form>
  );
};

export const JobNoteSheet = ({
  jobNote,
  onOpenChange,
  open,
}: {
  jobNote: JobNote | null;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) => {
  return (
    <Sheet
      onOpenChange={(open) => {
        onOpenChange(open);
      }}
      open={open}
    >
      <SheetContent
        // TODO: Better width handling?
        className="w-[70%] !min-w-[1000px] !max-w-[70%] overflow-y-auto p-0"
        hideClose
      >
        <Row className="items-center justify-between px-6 py-4">
          <SheetClose>
            <XIcon className="h-4 w-4 text-slate-700" />
          </SheetClose>
        </Row>
        <div className="border-t border-slate-200 p-6">
          <JobNoteForm
            jobNote={jobNote}
            handleSuccess={() => {
              onOpenChange(false);
            }}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};
